import { type RefObject, useState, useEffect, useRef } from 'react';
import { useInView, useMotionValue, useReducedMotion } from 'framer-motion';

const DEFAULT_DRAG_BUFFER = 25;
const DEFAULT_SLIDE_DURATION = 5000;

export type ResponsiveConfig = {
  breakpoint: number;
  slidesToShow?: number;
  slidesToScroll?: number;
  desiredSlideWidth?: number;
};

type UseCarouselOptions = {
  containerRef: RefObject<HTMLElement>;
  totalSlides: number;
  slidesToShow?: number;
  slidesToScroll?: number;
  desiredSlideWidth?: number;
  sizingGap?: number;
  responsive?: ResponsiveConfig[];
  infinite?: boolean;
  snapLastSlideToEnd?: boolean;
  autoPlay?: boolean;
  autoPlayDuration?: number;
  dragBuffer?: number;
  onSlideChange?: () => void;
  onNext?: () => void;
  onBack?: () => void;
};

export type UseCarouselReturn = {
  totalSlides: number;
  activeIndexes: number[];
  firstVisibleIndex: number;
  slidesToShow: number;
  slidesToScroll: number;
  maxFirstIndex: number;
  isNextDisabled: boolean;
  isBackDisabled: boolean;
  isDragging: boolean;
  isAutoPlayEnabled: boolean;
  isInfinite: boolean;
  internalIndex: number; // Accounts for clones in infinite mode
  cloneCount: number;
  shouldSkipAnimation: boolean; // Used for looping in infinite mode
  dragX: ReturnType<typeof useMotionValue<number>>;
  next: () => void;
  back: () => void;
  goToSlide: (index: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onAnimationComplete: () => void;
  startAutoPlay: () => void;
  stopAutoPlay: () => void;
};

export function getRenderedSlideCount(slidesToShow: number): number {
  return Math.max(1, Math.ceil(slidesToShow));
}

function createRawTrackIndexes(startIndex: number, count: number): number[] {
  return Array.from(
    { length: getRenderedSlideCount(count) },
    (_, i) => startIndex + i
  );
}

export function deriveSlidesToShowFromWidth(
  containerWidth: number,
  desiredSlideWidth: number,
  gap: number,
  totalSlides: number
): number {
  if (containerWidth <= 0 || desiredSlideWidth <= 0) {
    return 1;
  }

  return Math.max(
    1,
    Math.min(totalSlides, (containerWidth + gap) / (desiredSlideWidth + gap))
  );
}

export function getResponsiveValues(
  baseSlidesToShow: number,
  baseSlidesToScroll: number | undefined,
  baseDesiredSlideWidth: number | undefined,
  responsive: ResponsiveConfig[] | undefined,
  viewportWidth: number,
  containerWidth: number,
  gap: number,
  totalSlides: number
): { slidesToShow: number; slidesToScroll: number } {
  let slidesToShow = baseSlidesToShow;
  let slidesToScroll = baseSlidesToScroll;
  let desiredSlideWidth = baseDesiredSlideWidth;

  if (responsive && responsive.length > 0) {
    // Sort breakpoints ascending (mobile-first: apply settings for screens >= breakpoint)
    const sortedBreakpoints = [...responsive].sort(
      (a, b) => a.breakpoint - b.breakpoint
    );

    for (const config of sortedBreakpoints) {
      if (viewportWidth >= config.breakpoint) {
        if (config.slidesToShow !== undefined) {
          slidesToShow = config.slidesToShow;
          desiredSlideWidth = undefined;
        }
        if (config.slidesToScroll !== undefined) {
          slidesToScroll = config.slidesToScroll;
        }
        if (config.desiredSlideWidth !== undefined) {
          desiredSlideWidth = config.desiredSlideWidth;
        }
      }
    }
  }

  if (desiredSlideWidth !== undefined && containerWidth > 0) {
    slidesToShow = deriveSlidesToShowFromWidth(
      containerWidth,
      desiredSlideWidth,
      gap,
      totalSlides
    );

    if (slidesToScroll === undefined) {
      slidesToScroll = Math.max(1, Math.floor(slidesToShow));
    }
  }

  if (slidesToScroll === undefined) {
    slidesToScroll = slidesToShow;
  }

  return { slidesToShow, slidesToScroll };
}

// Hook to get responsive slidesToShow/slidesToScroll based on viewport
function useResponsiveSettings(
  containerRef: RefObject<HTMLElement>,
  totalSlides: number,
  baseSlidesToShow: number,
  baseSlidesToScroll: number | undefined,
  baseDesiredSlideWidth: number | undefined,
  sizingGap: number,
  responsive?: ResponsiveConfig[]
): { slidesToShow: number; slidesToScroll: number } {
  const [settings, setSettings] = useState(() =>
    getResponsiveValues(
      baseSlidesToShow,
      baseSlidesToScroll,
      baseDesiredSlideWidth,
      responsive,
      typeof window !== 'undefined' ? window.innerWidth : 0,
      containerRef.current?.getBoundingClientRect().width ?? 0,
      sizingGap,
      totalSlides
    )
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    function handleResize() {
      const newSettings = getResponsiveValues(
        baseSlidesToShow,
        baseSlidesToScroll,
        baseDesiredSlideWidth,
        responsive,
        window.innerWidth,
        containerRef.current?.getBoundingClientRect().width ?? 0,
        sizingGap,
        totalSlides
      );

      setSettings((prev) => {
        // Only update if values actually changed to avoid unnecessary re-renders
        if (
          prev.slidesToShow !== newSettings.slidesToShow ||
          prev.slidesToScroll !== newSettings.slidesToScroll
        ) {
          return newSettings;
        }
        return prev;
      });
    }

    handleResize();

    const element = containerRef.current;
    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== 'undefined' && element) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(element);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [
    containerRef,
    totalSlides,
    baseSlidesToShow,
    baseSlidesToScroll,
    baseDesiredSlideWidth,
    sizingGap,
    responsive,
  ]);

  return settings;
}

export function useCarousel({
  containerRef,
  totalSlides,
  slidesToShow: baseSlidesToShow = 1,
  slidesToScroll: baseSlidesToScroll,
  desiredSlideWidth,
  sizingGap = 0,
  responsive,
  infinite = false,
  snapLastSlideToEnd = false,
  autoPlay = false,
  autoPlayDuration = DEFAULT_SLIDE_DURATION,
  dragBuffer = DEFAULT_DRAG_BUFFER,
  onSlideChange,
  onNext,
  onBack,
}: UseCarouselOptions): UseCarouselReturn {
  // Get responsive settings
  const { slidesToShow, slidesToScroll: responsiveSlidesToScroll } =
    useResponsiveSettings(
      containerRef,
      totalSlides,
      baseSlidesToShow,
      baseSlidesToScroll,
      desiredSlideWidth,
      sizingGap,
      responsive
    );
  const slidesToScroll = responsiveSlidesToScroll;

  const cloneCount = infinite ? getRenderedSlideCount(slidesToShow) : 0;

  const [activeRawTrackIndexes, setActiveRawTrackIndexes] = useState(() =>
    createRawTrackIndexes(0, slidesToShow)
  );
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(autoPlay);
  const [isDragging, setIsDragging] = useState(false);
  const [shouldSkipAnimation, setShouldSkipAnimation] = useState(false);

  // Track if we need to loop after animation completes
  const pendingLoopRef = useRef<'start' | 'end' | null>(null);

  const shouldReduceMotion = useReducedMotion();
  const isInView = useInView(containerRef, { amount: 0.7 });
  const dragX = useMotionValue(0);

  // Track position is the raw position that can go into clone regions during animation
  const trackPosition = activeRawTrackIndexes[0] ?? 0;
  const maxFirstIndex = Math.max(
    0,
    snapLastSlideToEnd
      ? totalSlides - slidesToShow
      : Math.ceil(totalSlides - slidesToShow)
  );

  // In infinite mode, wrap to get the logical slide index
  const firstVisibleIndex = infinite
    ? ((trackPosition % totalSlides) + totalSlides) % totalSlides
    : trackPosition;
  const lastVisibleIndex = firstVisibleIndex + slidesToShow - 1;

  // In infinite mode, navigation is never disabled
  const isNextDisabled = infinite ? false : lastVisibleIndex >= totalSlides - 1;
  const isBackDisabled = infinite ? false : firstVisibleIndex <= 0;

  // Internal index accounts for clone offset in infinite mode
  // This is the actual position used for translateX calculations
  // Use trackPosition (not firstVisibleIndex) to support smooth animation through clones
  const internalIndex = infinite ? trackPosition + cloneCount : trackPosition;

  // Compute logical slide indexes for rendering (always valid 0 to totalSlides-1)
  const activeIndexes = infinite
    ? Array.from({ length: getRenderedSlideCount(slidesToShow) }, (_, i) => {
        const idx = (firstVisibleIndex + i) % totalSlides;
        return idx < 0 ? idx + totalSlides : idx;
      })
    : activeRawTrackIndexes;

  // Update activeRawTrackIndexes when slidesToShow changes (responsive)
  useEffect(() => {
    setActiveRawTrackIndexes((prev) => {
      const currentFirst = prev[0] ?? 0;
      const clampedFirst = Math.min(currentFirst, maxFirstIndex);
      return createRawTrackIndexes(clampedFirst, slidesToShow);
    });
  }, [slidesToShow, maxFirstIndex]);

  // Reset skip animation flag after it's been applied
  useEffect(() => {
    if (shouldSkipAnimation) {
      // Use requestAnimationFrame to ensure the skip was applied before resetting
      const id = requestAnimationFrame(() => {
        setShouldSkipAnimation(false);
      });
      return () => cancelAnimationFrame(id);
    }
  }, [shouldSkipAnimation]);

  function next() {
    if (!infinite) {
      setIsAutoPlayEnabled(false);
    }
    setActiveRawTrackIndexes((prev) => {
      const currentFirst = prev[0] ?? 0;

      if (infinite) {
        const newFirstIndex = currentFirst + slidesToScroll;
        // If we're going past the end, we'll animate to the clones and loop back
        if (newFirstIndex >= totalSlides) {
          pendingLoopRef.current = 'end';
        }
        return createRawTrackIndexes(newFirstIndex, slidesToShow);
      }

      const newFirstIndex = Math.min(
        currentFirst + slidesToScroll,
        maxFirstIndex
      );
      return createRawTrackIndexes(newFirstIndex, slidesToShow);
    });
    onSlideChange?.();
    onNext?.();
  }

  function snapBackIndex(rawIndex: number): number {
    if (!snapLastSlideToEnd) return rawIndex;
    return Math.ceil(rawIndex / slidesToScroll) * slidesToScroll;
  }

  function back() {
    if (!infinite) {
      setIsAutoPlayEnabled(false);
    }
    setActiveRawTrackIndexes((prev) => {
      const currentFirst = prev[0] ?? 0;

      if (infinite) {
        const newFirstIndex = currentFirst - slidesToScroll;
        // If we're going before the start, we'll animate to the clones and loop
        if (newFirstIndex < 0) {
          pendingLoopRef.current = 'start';
        }
        return createRawTrackIndexes(newFirstIndex, slidesToShow);
      }

      const newFirstIndex = Math.max(
        snapBackIndex(currentFirst - slidesToScroll),
        0
      );
      return createRawTrackIndexes(newFirstIndex, slidesToShow);
    });
    onSlideChange?.();
    onBack?.();
  }

  function goToSlide(index: number) {
    setIsAutoPlayEnabled(false);
    const clampedIndex = Math.max(0, Math.min(index, maxFirstIndex));
    setActiveRawTrackIndexes(createRawTrackIndexes(clampedIndex, slidesToShow));
  }

  // Called when slide animation completes - handles looping in infinite mode
  function onAnimationComplete() {
    if (!infinite || !pendingLoopRef.current) {
      return;
    }

    const loopDirection = pendingLoopRef.current;
    pendingLoopRef.current = null;

    // Skip animation for the loop
    setShouldSkipAnimation(true);

    if (loopDirection === 'end') {
      // We animated past the end to clones, loop to the real start
      const currentFirst = activeRawTrackIndexes[0] ?? 0;
      const wrappedIndex = currentFirst % totalSlides;
      setActiveRawTrackIndexes(
        createRawTrackIndexes(wrappedIndex, slidesToShow)
      );
    } else {
      // We animated before the start to clones, loop to the real end
      const currentFirst = activeRawTrackIndexes[0] ?? 0;
      // Calculate where we should be from the end
      const wrappedIndex = totalSlides + currentFirst;
      setActiveRawTrackIndexes(
        createRawTrackIndexes(wrappedIndex, slidesToShow)
      );
    }
  }

  function onDragStart() {
    if (!infinite) {
      setIsAutoPlayEnabled(false);
    }
    setIsDragging(true);
  }

  function onDragEnd() {
    const x = dragX.get();
    setIsDragging(false);

    // In infinite mode, always allow navigation
    const canGoNext = infinite || !isNextDisabled;
    const canGoBack = infinite || !isBackDisabled;

    if (x <= -dragBuffer && canGoNext) {
      setActiveRawTrackIndexes((prev) => {
        const currentFirst = prev[0] ?? 0;

        if (infinite) {
          const newFirstIndex = currentFirst + slidesToScroll;
          if (newFirstIndex >= totalSlides) {
            pendingLoopRef.current = 'end';
          }
          return createRawTrackIndexes(newFirstIndex, slidesToShow);
        }

        const newFirstIndex = Math.min(
          currentFirst + slidesToScroll,
          maxFirstIndex
        );
        return createRawTrackIndexes(newFirstIndex, slidesToShow);
      });
      onSlideChange?.();
      onNext?.();
    } else if (x >= dragBuffer && canGoBack) {
      setActiveRawTrackIndexes((prev) => {
        const currentFirst = prev[0] ?? 0;

        if (infinite) {
          const newFirstIndex = currentFirst - slidesToScroll;
          if (newFirstIndex < 0) {
            pendingLoopRef.current = 'start';
          }
          return createRawTrackIndexes(newFirstIndex, slidesToShow);
        }

        const newFirstIndex = Math.max(
          snapBackIndex(currentFirst - slidesToScroll),
          0
        );
        return createRawTrackIndexes(newFirstIndex, slidesToShow);
      });
      onSlideChange?.();
      onBack?.();
    }
  }

  useEffect(() => {
    if (!isAutoPlayEnabled || shouldReduceMotion) {
      return;
    }

    const autoPlayInterval = setInterval(() => {
      const x = dragX.get();

      if (!isInView || x !== 0) {
        return;
      }

      // Check if we've reached the end before computing new index
      const currentFirst = activeRawTrackIndexes[0] ?? 0;
      const newFirstIndex = currentFirst + slidesToScroll;

      // Compute new index
      setActiveRawTrackIndexes((prev) => {
        const prevFirst = prev[0] ?? 0;
        const nextFirst = prevFirst + slidesToScroll;

        if (infinite) {
          if (nextFirst >= totalSlides) {
            pendingLoopRef.current = 'end';
          }
          return createRawTrackIndexes(nextFirst, slidesToShow);
        }

        const clampedIndex = Math.min(nextFirst, maxFirstIndex);
        return createRawTrackIndexes(clampedIndex, slidesToShow);
      });

      // In non-infinite mode, stop at the end
      if (!infinite && newFirstIndex >= maxFirstIndex) {
        setIsAutoPlayEnabled(false);
      }

      onSlideChange?.();
      onNext?.();
    }, autoPlayDuration);

    return () => clearInterval(autoPlayInterval);
  }, [
    totalSlides,
    slidesToShow,
    slidesToScroll,
    maxFirstIndex,
    dragX,
    isInView,
    isAutoPlayEnabled,
    shouldReduceMotion,
    autoPlayDuration,
    onSlideChange,
    onNext,
    activeRawTrackIndexes,
    infinite,
  ]);

  const startAutoPlay = () => setIsAutoPlayEnabled(true);
  const stopAutoPlay = () => setIsAutoPlayEnabled(false);

  return {
    totalSlides,
    activeIndexes,
    firstVisibleIndex,
    slidesToShow,
    slidesToScroll,
    maxFirstIndex,
    isNextDisabled,
    isBackDisabled,
    isDragging,
    isAutoPlayEnabled,
    isInfinite: infinite,
    internalIndex,
    cloneCount,
    shouldSkipAnimation,
    dragX,
    next,
    back,
    goToSlide,
    onDragStart,
    onDragEnd,
    onAnimationComplete,
    startAutoPlay,
    stopAutoPlay,
  };
}
